import { Component, createRef } from 'react'
import { instanceOf, shape, string } from 'prop-types'
import cn from 'classnames'

import preloadPostMedia from '../../../../utilities/preloadPostMedia.js'
import Slick from './slick/slick.js'

import GateContext from '../../../../utilities/gateContext.js'
import sendTrackingEvent from '../../../../utilities/sendTrackingEvent.js'
import getPostWidth from './helper-functions/getPostWidth.js'
import Banner from '../../theme-components/banner.js'
import DesktopCarouselControl from './desktop-carousel-control.js'
import CarouselFeedPost from './carousel-feed-post.js'
import isDark from '../../../../utilities/isDark.js'
import loadWebFont from '../../../../utilities/loadWebFont.js'
import MobileCarouselControl from './mobile-carousel-control.js'
import Settings from '../../../../../models/settings.js'

import { VisiblePostsContext } from '../../../../contexts/visiblePostsContext.js'

import '../../../../../styles/themes/carousel/carousel.scss'

export default class CarouselTheme extends Component {
  static propTypes = {
    containerElement: shape().isRequired,
    context: string,
    feed: shape().isRequired,
    feedKey: string.isRequired,
    settings: instanceOf(Settings).isRequired,
  }

  // eslint-disable-next-line react/sort-comp
  static contextType = VisiblePostsContext

  static defaultProps = {
    context: undefined,
  }

  constructor(props) {
    super(props)

    const width = props.settings.containerWidth || props.containerElement?.offsetWidth

    this.slickRef = createRef()
    this.wrapperRef = createRef()

    this.state = {
      containerWidth: width,
      wrapperWidth: 0,
      autoplaySpeed: 8,
      hasLeftCarousel: false,
      slickAutoplay: false,
      observer: new IntersectionObserver(
        (entries) => {
          const sortedPostKeys = new Map()
          entries.forEach((entry) => {
            const { target } = entry
            const { postKey } = target.dataset
            if (entry.isIntersecting) {
              sortedPostKeys.set(postKey, true)
            } else if (!sortedPostKeys.has(postKey)) {
              sortedPostKeys.set(postKey, false)
            }
          })
          const { addVisiblePostId, removeVisiblePostId } = this.context
          sortedPostKeys.forEach((isVisible, postKey) => {
            if (isVisible) {
              addVisiblePostId(postKey)
            } else {
              removeVisiblePostId(postKey)
            }
          })
        },
        {
          root: (this.slickRef.current?.innerSlider?.list || this.wrapperRef).current,
          threshold: 0.2,
        },
      ),
    }
  }

  componentDidMount() {
    loadWebFont(this.props.settings)
    window.addEventListener('resize', this.handleResize, { passive: true })
    this.handleResize()

    if (this.props.settings.theme === 'carousel-auto') {
      const autoplaySpeed = this.props.settings.highlightTimeout
      this.setState({ slickAutoplay: true, autoplaySpeed })
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  get feedStyles() {
    const { settings } = this.props
    return {
      fontFamily: settings.fontFamily,
      fontSize: settings.feedFontSize,
      backgroundColor: settings.feedBackgroundColor,
    }
  }

  getSlickSettings(filteredPosts) {
    const { settings } = this.props
    const { locale } = settings
    const isMobileView = this.state.containerWidth <= 480
    const numberOfVisiblePosts = Math.floor(this.state.wrapperWidth / this.postWidth)
    let slidesToShow = isMobileView ? settings.mobileSlidesToShow : numberOfVisiblePosts

    if (settings.slidesToShowEnabled) {
      slidesToShow = isMobileView
        ? settings.mobileSlidesToShow
        : Math.min(settings.slidesToShow, numberOfVisiblePosts)
    }

    if (Number.isNaN(slidesToShow) || slidesToShow < 1) {
      slidesToShow = 1
    }

    const baseSettings = {
      autoplay: this.state.slickAutoplay,
      speed: 250,
      slidesToScroll: 1,
      slidesToShow,
      initialSlide: 0,
      infinite: filteredPosts.length > numberOfVisiblePosts,
      rtl: locale.isRTL,
      slide: '.bzfy-c-carousel-slide-wrap',
    }

    if (this.props.settings.theme === 'carousel-auto') {
      baseSettings.autoplaySpeed = this.state.autoplaySpeed * 1000
    }

    return baseSettings
  }

  get postWidth() {
    return getPostWidth(this.state.wrapperWidth, this.props.settings)
  }

  handleResize = () => {
    const { props, wrapperRef } = this
    const containerWidth = props.settings.containerWidth || props.containerElement?.offsetWidth
    const wrapper = wrapperRef?.current
    let wrapperWidth = 0
    if (wrapper) {
      const style = getComputedStyle(wrapper)
      wrapperWidth =
        wrapper.clientWidth -
          parseFloat(style.getPropertyValue('padding-left')) -
          parseFloat(style.getPropertyValue('padding-right')) || containerWidth
    }
    this.setState({
      containerWidth,
      wrapperWidth,
    })
  }

  trackUserInteraction = () => {
    const { allowCookies, enableWebAnalytics } = this.props.settings
    sendTrackingEvent({
      allowCookies,
      enableWebAnalytics,
      flowKey: this.props.feedKey,
      action: 'click',
      target: 'flow',
      eventName: 'embed_carousel_next_post_clicked',
      eventLabel: 'carousel',
      eventAction: 'clicked',
      interactive: true,
    })
  }

  slidePrev = () => {
    this.trackUserInteraction()
    this.slickRef.current.slickPrev()
  }

  slideNext = () => {
    this.trackUserInteraction()
    this.slickRef.current.slickNext()
  }

  slideTo = (slideNumber) => {
    this.trackUserInteraction()
    this.slickRef.current.slickGoTo(slideNumber)
  }

  toggleAutoPlay = (toggle) => {
    this.setState({ slickAutoplay: toggle })
  }

  toggleHasLeftCarousel = () => {
    this.setState((prevstate) => ({ hasLeftCarousel: !prevstate.hasLeftCarousel }))
  }

  leaveCarousel = () => {
    this.toggleHasLeftCarousel()
    if (this.props.settings.theme !== 'carousel-auto') {
      document.querySelector('.bzfy-c-carousel-control').focus()
    }
  }

  renderCarouselControls() {
    const { settings } = this.props
    const { feedBackgroundColor } = settings
    const backgroundIsDark = isDark(feedBackgroundColor)

    const { isRTL } = settings.locale
    const leftClickHandler = isRTL ? this.slideNext : this.slidePrev
    const rightClickHandler = isRTL ? this.slidePrev : this.slideNext

    if (this.state.containerWidth <= 480) {
      return (
        <MobileCarouselControl
          aria-controls="carousel"
          aria-label="carousel control buttons"
          backgroundIsDark={backgroundIsDark}
          leftFunc={leftClickHandler}
          rightFunc={rightClickHandler}
        />
      )
    }
    return (
      <DesktopCarouselControl
        aria-controls="carousel"
        aria-label="carousel control buttons"
        backgroundIsDark={backgroundIsDark}
        leftFunc={leftClickHandler}
        rightFunc={rightClickHandler}
      />
    )
  }

  renderFeedContent() {
    const { feed, settings } = this.props
    const { gutterSize, hasDisplayLimit, locale, postsToShow } = settings
    const displayLimit = hasDisplayLimit ? postsToShow : 29

    const filteredPosts = feed.posts.filter((p) => p.media.length > 0).slice(0, displayLimit)
    if (filteredPosts.length === 0) {
      return null
    }

    const slickSettings = this.getSlickSettings(filteredPosts)
    const padding = gutterSize / 2
    const maxWidth = filteredPosts.length * this.postWidth + gutterSize + 160
    const ariaLive = slickSettings.autoplay ? 'polite' : 'off'
    const shouldRenderControls =
      !settings.isAutoTheme && filteredPosts.length > slickSettings.slidesToShow

    const openPost = (post, openGate) => {
      if (this.slickRef.current.innerSlider.clickable) {
        openGate(post)
      }
    }

    return (
      <GateContext.Consumer>
        {({ openGate }) => (
          <div aria-live={ariaLive} className="bzfy-c-carousel-slider-wrapper">
            <div
              ref={this.wrapperRef}
              className="bzfy-c-carousel-slider-wrapper__inner"
              style={{ display: 'inline-block', width: '100%' }}
            >
              <Slick {...slickSettings} ref={this.slickRef}>
                {filteredPosts.map((post, index) => (
                  <div
                    key={`post-${post.key}`}
                    aria-label={`${index + 1}/${filteredPosts.length}`}
                    className={cn(
                      'bzfy-c-carousel-slide-wrap',
                      'bzfy-c-carousel-slide-wrap--clickable',
                    )}
                    id={`post-${index}`}
                    onMouseEnter={() => preloadPostMedia(post)}
                  >
                    <div style={{ paddingLeft: padding, paddingRight: padding }}>
                      <CarouselFeedPost
                        feedKey={this.props.feedKey}
                        goToPost={this.slideTo}
                        hasLeftCarousel={this.state.hasLeftCarousel}
                        leaveCarousel={this.leaveCarousel}
                        locale={locale}
                        maxPosts={filteredPosts.length}
                        observer={this.state.observer}
                        openPost={() => openPost(post, openGate)}
                        pauseAutoplay={this.pauseAutoplay}
                        post={post}
                        postIndex={index}
                        postWidth={maxWidth / filteredPosts.length + this.postWidth}
                        settings={settings}
                        slickAutoplay={this.state.slickAutoplay}
                        toggleAutoPlay={this.toggleAutoPlay}
                        toggleHasLeftCarousel={this.toggleHasLeftCarousel}
                      />
                    </div>
                  </div>
                ))}
              </Slick>
              {shouldRenderControls && this.renderCarouselControls()}
            </div>
          </div>
        )}
      </GateContext.Consumer>
    )
  }

  render() {
    const hasPosts = this.props.feed?.posts?.length !== 0
    if (!hasPosts) {
      return null
    }

    const { settings } = this.props
    const { containerWidth } = this.state
    const fixedHeight = settings.containerFixedHeight

    return (
      <div className="bzfy-t-feed-carousel" data-testid={settings.theme} style={this.feedStyles}>
        <div
          className={cn('bzfy-feed', {
            'bzfy-feed--fixed': !!fixedHeight,
            'bzfy-feed--has-banner-top': settings.showTopBanner,
          })}
          style={{ height: fixedHeight || 'auto' }}
        >
          {settings.showTopBanner && hasPosts ? <Banner settings={settings} /> : null}
          <div className="bzfy-o-container" style={{ maxWidth: containerWidth }}>
            {this.renderFeedContent()}
          </div>
          {settings.showBottomBanner && hasPosts ? <Banner settings={settings} /> : null}
        </div>
      </div>
    )
  }
}
